import React, { forwardRef, type ReactNode } from 'react';

import { cssMap, cx } from '@atlaskit/css';

import { Box } from '@atlaskit/primitives/compiled/box';

import { Inline } from '@atlaskit/primitives/compiled/inline';

import { Stack } from '@atlaskit/primitives/compiled/stack';

import { Text } from '@atlaskit/primitives/compiled/text';

import { token } from '@atlaskit/tokens';

import Link from '@atlaskit/link/link';

import { TouchScrollable } from 'react-scrolllock';

import { type ModalProps } from './types';

import Heading from '@atlaskit/heading/heading';

import { FormattedMessage } from 'react-intl';

import CrossIcon from '@atlaskit/icon/core/cross';

import IconButton from '@atlaskit/button/icon/button';

import { messages } from './messages';

import { READ_ABOUT_URL } from './contants';

import { JiraIcon } from '@atlaskit/logo';

const styles = cssMap({
	baseContainer: {
		position: 'relative',
		overflowY: 'auto',
		height: '100%',
		borderRadius: token('radius.medium', '3px'),
		'@media (min-width: 64rem)': {
			display: 'grid',
			gridTemplateColumns: '273px 695px',
		},
	},
	stack: {
		height: '100%',
		paddingTop: token('space.600'),
		paddingRight: token('space.600'),
		paddingBottom: token('space.600'),
		paddingLeft: token('space.600'),
		overflowY: 'auto',
	},
	sideContainer: {
		display: 'none',
		'@media (min-width: 64rem)': {
			backgroundColor: token('color.background.accent.gray.subtlest'),
			display: 'flex',
			width: '100%',
			overflowY: 'auto',
		},
	},
	closeButton: {
		position: 'absolute',
		top: token('space.200'),
		right: token('space.200'),
		zIndex: 1,
	},
	footerWrap: {
		flexWrap: 'wrap-reverse',
	},
	scrollableContainer: {
		overflowY: 'auto',
		flex: '1 1 auto',
		paddingRight: token('space.050'),
		paddingLeft: token('space.050'),
	},
});

const ScrollableBody: React.ForwardRefExoticComponent<{ children: ReactNode }> = forwardRef<
	HTMLElement,
	{ children: ReactNode }
>(({ children }, ref) => {
	return (
		<Stack
			space="space.250"
			xcss={styles.scrollableContainer}
			testId="timeline-onboardingmodal-scrollablebody"
			ref={ref}
		>
			{children}
		</Stack>
	);
});

export const Modal = ({
	title,
	content,
	actions,
	sideContent,
	onClose,
}: ModalProps): React.JSX.Element => {
	return (
		<Box xcss={cx(styles.baseContainer)}>
			<Box xcss={styles.closeButton}>
				<IconButton
					icon={CrossIcon}
					onClick={onClose}
					label={<FormattedMessage {...messages.closeButton} />}
					testId="timeline-onboardingmodal-close-button"
				/>
			</Box>
			<Stack space="space.400" xcss={styles.stack}>
				<JiraIcon appearance="brand" size="small" />
				<Heading testId="timeline-onboardingmodal-title" size="xxlarge">
					{title}
				</Heading>
				<TouchScrollable>
					<ScrollableBody>
						<Text>{content}</Text>
						<Link href={READ_ABOUT_URL} target="_blank">
							<FormattedMessage {...messages.timelineImprovmentsLink} />
						</Link>
					</ScrollableBody>
				</TouchScrollable>
				<Inline
					alignInline="end"
					testId="timeline-onboardingmodal-footer"
					space="space.150"
					xcss={styles.footerWrap}
				>
					{actions}
				</Inline>
			</Stack>
			<Box xcss={styles.sideContainer}>{sideContent}</Box>
		</Box>
	);
};
